import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { chord as d3chord, ribbon as d3ribbon } from 'd3-chord';
import { arc as d3arc } from 'd3-shape';

// Max tags to show — chord diagrams get unreadable past ~40
const MAX_TAGS = 40;

// Hue-spaced palette matching ClusterGraph for visual consistency
const PALETTE = [
  '#6366f1','#10b981','#f59e0b','#ef4444','#0095FF','#8b5cf6',
  '#06b6d4','#f97316','#84cc16','#ec4899','#14b8a6','#a855f7',
  '#e11d48','#0ea5e9','#65a30d','#d97706','#7c3aed','#059669',
  '#dc2626','#0569C9',
];

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

const ChordDiagram = ({ bookmarks, onNavigate, isDark = false }) => {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [containerWidth, setContainerWidth] = useState(600);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => setContainerWidth(entries[0].contentRect.width));
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // ── Data: co-occurrence matrix ────────────────────────────────────────────
  // Pick the top MAX_TAGS tags by usage frequency
  const { tags, matrix, tagColors } = useMemo(() => {
    if (!bookmarks.length) return { tags: [], matrix: [], tagColors: [] };

    // Count frequency
    const freq = new Map();
    bookmarks.forEach(b => (b.tags || []).forEach(t => freq.set(t, (freq.get(t) || 0) + 1)));

    const tags = Array.from(freq.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, MAX_TAGS)
      .map(([t]) => t);

    if (tags.length < 2) return { tags, matrix: [], tagColors: [] };

    const idx = new Map(tags.map((t, i) => [t, i]));
    const n = tags.length;
    const matrix = Array.from({ length: n }, () => new Array(n).fill(0));

    bookmarks.forEach(b => {
      const bmTags = (b.tags || []).filter(t => idx.has(t));
      for (let i = 0; i < bmTags.length; i++) {
        for (let j = i + 1; j < bmTags.length; j++) {
          const a = idx.get(bmTags[i]), bIdx = idx.get(bmTags[j]);
          matrix[a][bIdx]++;
          matrix[bIdx][a]++;
        }
      }
    });

    const tagColors = tags.map((_, i) => PALETTE[i % PALETTE.length]);
    return { tags, matrix, tagColors };
  }, [bookmarks]);

  // ── D3 chord layout ───────────────────────────────────────────────────────
  const chordData = useMemo(() => {
    if (matrix.length < 2) return null;
    const chordLayout = d3chord().padAngle(0.04).sortSubgroups((a, b) => b - a);
    return chordLayout(matrix);
  }, [matrix]);

  // ── Dimensions ────────────────────────────────────────────────────────────
  const size = Math.min(containerWidth, 560);
  const outerRadius = size / 2 - 40;
  const innerRadius = outerRadius - 20;

  const arcGen = useMemo(() => d3arc().innerRadius(innerRadius).outerRadius(outerRadius), [innerRadius, outerRadius]);
  const ribbonGen = useMemo(() => d3ribbon().radius(innerRadius), [innerRadius]);

  // ── Tooltip state ─────────────────────────────────────────────────────────
  const [tooltip, setTooltip] = useState(null); // {x, y, text}
  const svgRef = useRef(null);

  const handleArcEnter = useCallback((e, i) => {
    setHoveredIdx(i);
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const count = matrix[i].reduce((s, v) => s + v, 0) / 2; // each pair counted twice
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top - 8,
      text: `${tags[i]}: co-occurs in ${Math.round(count)} bookmark pair${Math.round(count) !== 1 ? 's' : ''}`,
    });
  }, [matrix, tags]);

  const handleArcLeave = useCallback(() => {
    setHoveredIdx(null);
    setTooltip(null);
  }, []);

  const handleChordEnter = useCallback((e, chord) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const si = chord.source.index, ti = chord.target.index;
    const count = chord.source.value;
    setTooltip({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top - 8,
      text: `${tags[si]} + ${tags[ti]}: ${count} shared bookmark${count !== 1 ? 's' : ''}`,
    });
  }, [tags]);

  const handleChordLeave = useCallback(() => setTooltip(null), []);

  // ── Edge cases ────────────────────────────────────────────────────────────
  if (!bookmarks.length) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center text-gray-500 dark:text-gray-400">
        No bookmarks to display.
      </div>
    );
  }

  if (tags.length < 2) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center text-gray-500 dark:text-gray-400">
        <p className="font-medium text-gray-700 dark:text-gray-300 mb-1">Not enough tags.</p>
        <p className="text-sm">Add tags to at least 2 bookmarks to see the chord diagram.</p>
      </div>
    );
  }

  if (!chordData) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 text-center text-gray-500 dark:text-gray-400">
        No tag co-occurrences found. Tags need to appear together on the same bookmarks.
      </div>
    );
  }

  const cx = size / 2, cy = size / 2;

  // Label angle for each group: midpoint of the arc
  const labelRadius = outerRadius + 14;

  return (
    <div className="space-y-4">
      {tags.length === MAX_TAGS && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400 text-sm rounded-lg px-4 py-2">
          Showing top {MAX_TAGS} tags by usage. Less-frequent tags are omitted.
        </div>
      )}

      <div className="flex gap-4 items-start">
        {/* Legend */}
        <div className="w-44 flex-shrink-0 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 self-start">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Tags</p>
          <div className="space-y-1.5 max-h-80 overflow-y-auto">
            {tags.map((tag, i) => (
              <button
                key={tag}
                className={`flex items-center gap-2 w-full text-left rounded px-1 py-0.5 transition-colors ${
                  hoveredIdx === i ? 'bg-gray-50 dark:bg-gray-700' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                onClick={() => onNavigate?.(tag)}
                title={`Filter bookmarks by "${tag}"`}
              >
                <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: tagColors[i] }} />
                <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{tag}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 leading-relaxed">
            Arc size = co-occurrence count<br />
            Chord = shared bookmarks<br />
            Click tag to filter
          </p>
        </div>

        {/* SVG chord diagram */}
        <div ref={containerRef} className="flex-1 min-w-0 bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden relative">
          <svg
            ref={svgRef}
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            className="block mx-auto"
          >
            <g transform={`translate(${cx},${cy})`}>
              {/* Ribbons (chords) */}
              {chordData.map((chord, ci) => {
                const si = chord.source.index;
                const ti = chord.target.index;
                const isHovered = hoveredIdx === si || hoveredIdx === ti;
                const isDimmed = hoveredIdx !== null && !isHovered;
                const color = tagColors[si];
                return (
                  <path
                    key={ci}
                    d={ribbonGen(chord)}
                    fill={hexToRgba(color, isDimmed ? 0.04 : isHovered ? 0.55 : 0.2)}
                    stroke={hexToRgba(color, isDimmed ? 0.08 : isHovered ? 0.8 : 0.35)}
                    strokeWidth={0.5}
                    style={{ cursor: 'default', transition: 'fill 0.15s, stroke 0.15s' }}
                    onMouseEnter={e => handleChordEnter(e, chord)}
                    onMouseLeave={handleChordLeave}
                  />
                );
              })}

              {/* Arc segments */}
              {chordData.groups.map((group, i) => {
                const isHovered = hoveredIdx === i;
                const isDimmed = hoveredIdx !== null && !isHovered;
                const color = tagColors[i];
                const midAngle = (group.startAngle + group.endAngle) / 2 - Math.PI / 2;
                const lx = Math.cos(midAngle) * labelRadius;
                const ly = Math.sin(midAngle) * labelRadius;
                const anchor = Math.cos(midAngle) > 0 ? 'start' : 'end';
                const tag = tags[i];
                const labelText = tag.length > 16 ? tag.slice(0, 14) + '\u2026' : tag;

                return (
                  <g key={i}>
                    <path
                      d={arcGen(group)}
                      fill={hexToRgba(color, isDimmed ? 0.25 : 0.85)}
                      stroke={isDark ? '#111827' : 'white'}
                      strokeWidth={1}
                      style={{ cursor: 'pointer', transition: 'fill 0.15s' }}
                      onMouseEnter={e => handleArcEnter(e, i)}
                      onMouseLeave={handleArcLeave}
                      onClick={() => onNavigate?.(tag)}
                    />
                    {/* Label — only show when not too crowded */}
                    {(group.endAngle - group.startAngle) > 0.12 && (
                      <text
                        x={lx}
                        y={ly}
                        textAnchor={anchor}
                        dominantBaseline="middle"
                        fontSize={10}
                        fill={isDimmed ? '#9ca3af' : (isDark ? '#e2e8f0' : '#374151')}
                        style={{ pointerEvents: 'none', userSelect: 'none', transition: 'fill 0.15s' }}
                      >
                        {labelText}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          </svg>

          {/* Tooltip */}
          {tooltip && (
            <div
              className="absolute pointer-events-none bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg"
              style={{ left: tooltip.x, top: tooltip.y, transform: 'translate(-50%, -100%)' }}
            >
              {tooltip.text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChordDiagram;
